import Head from 'next/head'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { serverSideConfigs, t } from 'localization'
import { serverRequestService } from 'server/services/serverRequestService'
import { PagesQA } from 'shared/dataAttributes'
import { colors, fonts, media, styles } from 'shared/lib'
import { Store } from 'shared/types/Store'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import { FlightClubStore } from 'stores/components/FlightClubStore'

const TP = 'pages.storelocator'

type StoresPageProps = {
  stores: Store[]
}

const StoresPage = ({ stores }: StoresPageProps) => {
  const [flightClubStores, setFlightClubStores] = useState<Store[]>(stores || [])
  useEffect(() => {
    if (stores) {
      const sorted = [...stores].sort((a: Store, b: Store) => a?.id - b?.id)

      setFlightClubStores(sorted)
    }
  }, [stores])

  return (
    <>
      <Head>
        <title>{t(`${TP}.pageTitle`, 'Stores | Flight Club')}</title>
      </Head>
      <Wrapper>
        <Header data-qa={PagesQA.StoresPageTitle}>{t(`${TP}.stores`, 'Stores')}</Header>
        <Body>
          {flightClubStores.map((store) => {
            return <FlightClubStore key={store.id} {...store} />
          })}
        </Body>
      </Wrapper>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { res, req, locale } = context
  try {
    const { data } = await serverRequestService(req, res, 'sneakers').get(
      '/api/v1/flightclub_stores',
    )
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        stores: data.flightclubStores,
      },
    }
  } catch (error) {
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        notFound: true,
      },
    }
  }
}

const Wrapper = styled.div`
  background-color: ${colors.FC2_WHITE};
  height: 100%;
  padding: ${styles.mobilePageLayoutPadding};
  ${media.large`
    padding: 80px 150px 100px;
  `}
`

const Header = styled.h1`
  ${fonts.HEADER_1}
  margin-top: 0;
`

const Body = styled.div`
  display: grid;
  grid-gap: 15px;
  grid-template-columns: 1fr;
  ${media.large`
    grid-template-columns: 1fr 1fr;
  `}
`

export default StoresPage
